import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, ExportOutlined, ImportOutlined, RedoOutlined } from '@ant-design/icons';
import { blue } from '@ant-design/colors';
import API from '../API';
import { getStats } from '../dataProviders';
import { useUserLevel } from './use-user-level';
import { useUserId } from './use-user-id';
import { DeliveredLooseOrderView, PanelStatsView } from 'types';

export const usePanel = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [stats, setStats] = useState<PanelStatsView>(location.state?.stats);
	const [showToClaim, setShowToClaim] = useState(false);
	const [showToClaimLoose, setShowToClaimLoose] = useState(false);
	const [showUnclosed, setShowUnclosed] = useState(false);
	const [balance, setBalance] = useState(0);
	const [deliveredLooseOrders, setDeliveredLooseOrders] = useState<DeliveredLooseOrderView[]>();

	const userId = useUserId();
	const level = useUserLevel()!;

	const fetch = async () => {
		if (!userId) {
			navigate('/logout', { replace: true });
			return;
		}

		setStats(await getStats(location));
		setDeliveredLooseOrders(await API.looseOrders.getDelivered());

		const user = await API.users.get(userId, undefined);
		setBalance(user.balance);
	};

	useEffect(() => {
		if (location.pathname !== '/panel') return;

		fetch();

		const interval = setInterval(fetch, 30 * 1000);

		return () => clearInterval(interval);
	}, [location, userId]);

	const getTaskIcon = (task: any) => {
		if (!task) return <CloseCircleOutlined style={{ color: blue[5] }} />;

		switch (task.type) {
			// Delivery
			case 0:
				return <ImportOutlined style={{ color: blue[5] }} />;

			// Replacement
			case 1:
				return <RedoOutlined style={{ color: blue[5] }} />;

			// Claiming
			case 2:
				return <ExportOutlined style={{ color: blue[5] }} />;
		}

		throw new Error('Invalid task type');
	};

	const getTaskDescription = (task: any) => {
		switch (task.type) {
			// Delivery
			case 0:
				return `Podstawienie ${task.order.street}`;

			// Replacement
			case 1:
				return `Wymiana ${task.order.street}`;

			// Claiming
			case 2:
				return `Odbiór ${task.order.street}`;
		}

		throw new Error('Invalid task type');
	};

	return {
		stats,
		deliveredLooseOrders,
		balance,
		admin: level > 0,
		level,
		showToClaim,
		showToClaimLoose,
		showUnclosed,
		getTaskIcon,
		getTaskDescription,
		setShowToClaim,
		setShowToClaimLoose,
		setShowUnclosed,
	};
};
